import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getStaffReturnPath, getStaffSection } from '../../../../app/router/staffNavigation';
import { useAppSelector } from "../../../../app/store/store";
import { IIdmRole, idmService } from "../../../../entities/idm";
import { FEATURES, hasFeature } from "../../../../entities/my";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { MyError } from "../../../../shared/lib/error/MyError";
import { getStaffPermissions } from "../../model/getStaffPermissions";
import { useStaffAccessManagement } from "../../model/useStaffAccessManagement";
import { useStaffFeedback } from "../../model/useStaffFeedback";
import { useStaffMember } from "../../model/useStaffMember";
import { StaffAccessContent } from "../staffAccess/StaffAccessContent";
import { StaffAccessModals } from "../staffAccess/StaffAccessModals";
import { StaffDetailLayout } from "../staffDetail/StaffDetailLayout";

export const StaffIdm = () => {
    const navigate = useNavigate()
    const {pathname, search} = useLocation()
    const backPath = getStaffReturnPath(search, getStaffSection(pathname))
    const {user_id} = useParams<{user_id: string}>()
    const staffId = user_id && !Number.isNaN(Number(user_id)) ? Number(user_id) : null
    const {my} = useAppSelector(state => state.myReducer)
    const permissions = getStaffPermissions(my, staffId || undefined)
    const canReadScheduleHistory = Boolean(staffId) && hasFeature(my, FEATURES.AUDIT.SCHEDULE_HISTORY_READ, staffId || undefined)
    const {handleError, showError, showSuccess} = useStaffFeedback()
    const [roles, setRoles] = useState<IIdmRole[]>([])
    const [canShowAssignRoles, setCanShowAssignRoles] = useState(true)

    const onNotFound = useCallback(() => {
        navigate(backPath, {replace: true})
        showError('Сотрудник не найден')
    }, [backPath, navigate, showError])

    const {
        isLoading,
        loadError,
        loadStaffMember,
        setStaffMember,
        staffMember,
    } = useStaffMember({staffId, onError: handleError, onNotFound})

    const canAssignIdmRoles = !staffMember.roles.some(role => role.role_code.trim().toLowerCase() === 'tutor')
    const isTutorStaff = staffMember.roles.some(role => role.role_code.trim().toLowerCase() === 'tutor')

    useEffect(() => {
        if(!staffId || !permissions.canListRoles || !canAssignIdmRoles){
            setRoles([])
            setCanShowAssignRoles(true)
            return
        }

        let isActual = true

        setCanShowAssignRoles(true)

        idmService.listAvailableRolesToAdd(staffId)
            .then(response => {
                if(isActual){
                    setRoles(response)
                }
            })
            .catch(error => {
                if(isActual){
                    if((error instanceof MyError || error instanceof AuthError) && error.status === 403){
                        setRoles([])
                        setCanShowAssignRoles(false)
                        return
                    }
                    handleError(error, 'Ошибка при получении ролей IDM')
                }
            })

        return () => {
            isActual = false
        }
    }, [canAssignIdmRoles, handleError, permissions.canListRoles, staffId])

    const access = useStaffAccessManagement({
        enabled: true,
        selectedStaffId: staffId,
        staffMember,
        setStaffMember,
        roles,
        canListUserFeatures: permissions.canListUserFeatures,
        canListAvailableFeatures: permissions.canListAvailableFeatures,
        refreshBase: loadStaffMember,
        onError: handleError,
        onSuccess: showSuccess,
    })

    const isContentLoading = Boolean(staffId) && !loadError && (isLoading || staffMember.user_id !== staffId)

    return (
        <StaffDetailLayout
            staffId={staffId}
            isLoading={isContentLoading}
            loadError={loadError}
            selectedTab={2}
            showSwitch={permissions.canReadIdmStaff}
            showPaymentDetailsTab={isTutorStaff}
            showHistoryTab={isTutorStaff && canReadScheduleHistory}
        >
            <StaffAccessContent
                staffMember={staffMember}
                permissions={permissions}
                access={access}
                showRolesWidget={canShowAssignRoles}
                canAssignIdmRoles={canAssignIdmRoles && canShowAssignRoles}
                canListRoles={permissions.canListRoles}
            />
            <StaffAccessModals access={access} />
        </StaffDetailLayout>
    )
}
